import { Card, Direction } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { isRedSuit } from '../../engine/CardSuits';
import { IHandSortingStrategy } from './IHandSortingStrategy';

/**
 * Universal base hand sorting strategy.
 * Enforces tournament-standard alternating Red/Black / Black/Red suit ordering across hands
 * to maximize visual contrast and eliminate misplays.
 */
export abstract class BaseHandSortingStrategy implements IHandSortingStrategy {
  public abstract sortHand(
    hand: Card[],
    direction?: Direction | string | null,
    trumpSuit?: string | null,
    config?: GameTableConfig
  ): Card[];

  /**
   * Returns the color ('red' | 'black') of a card suit.
   */
  public getSuitColor(suit: string): 'red' | 'black' {
    return isRedSuit(suit) ? 'red' : 'black';
  }

  /**
   * Determines optimal tournament suit arrangement with alternating Black and Red suits.
   * If an active trump suit is declared, non-trump suits alternate colors and transition
   * cleanly into the trump suit placed at the far right.
   * If no trump suit is active, suits alternate: Black -> Red -> Black -> Red (or Red -> Black -> Red -> Black).
   */
  public getAlternatingSuitOrder(
    presentNonTrumpSuits: string[],
    normTrump: string | null
  ): string[] {
    const hasTrumpSuit = Boolean(
      normTrump &&
      normTrump !== 'NoTrump' &&
      normTrump !== 'NT' &&
      normTrump !== 'NO_TRUMP'
    );

    if (hasTrumpSuit) {
      const trumpColor = this.getSuitColor(normTrump!);

      if (presentNonTrumpSuits.length === 3) {
        // 4 suits total in hand: Alternate colors ending with opposite of trumpColor
        // If Trump is Black: [Red, Black, Red] -> transitions to Black Trump (e.g. Diamonds, Clubs, Hearts -> Spades)
        // If Trump is Red: [Black, Red, Black] -> transitions to Red Trump (e.g. Clubs, Hearts, Spades -> Diamonds)
        const canonical: string[] =
          normTrump === 'Spades' ? ['Hearts', 'Clubs', 'Diamonds'] :
          normTrump === 'Clubs' ? ['Hearts', 'Spades', 'Diamonds'] :
          normTrump === 'Hearts' ? ['Spades', 'Diamonds', 'Clubs'] :
          ['Spades', 'Hearts', 'Clubs'];
        return canonical.filter(s => presentNonTrumpSuits.includes(s));
      }

      if (presentNonTrumpSuits.length === 2) {
        const [s1, s2] = presentNonTrumpSuits;
        const c1 = this.getSuitColor(s1);
        const c2 = this.getSuitColor(s2);

        if (c1 !== c2) {
          const redSuit = c1 === 'red' ? s1 : s2;
          const blackSuit = c1 === 'black' ? s1 : s2;

          if (trumpColor === 'black') {
            // Trump is Black (far right). Alternating: Black -> Red -> Trump(Black)
            return [blackSuit, redSuit];
          } else {
            // Trump is Red (far right). Alternating: Red -> Black -> Trump(Red)
            return [redSuit, blackSuit];
          }
        }

        const canonical: string[] = ['Spades', 'Hearts', 'Clubs', 'Diamonds'];
        return canonical.filter(s => presentNonTrumpSuits.includes(s));
      }

      return presentNonTrumpSuits;
    }

    // No trump active: generic alternating Black -> Red -> Black -> Red:
    // Spades (B), Hearts (R), Clubs (B), Diamonds (R)
    if (presentNonTrumpSuits.length === 4) {
      return ['Spades', 'Hearts', 'Clubs', 'Diamonds'];
    }

    if (presentNonTrumpSuits.length === 3) {
      const blacks = presentNonTrumpSuits.filter(s => this.getSuitColor(s) === 'black');
      const reds = presentNonTrumpSuits.filter(s => this.getSuitColor(s) === 'red');

      if (blacks.length === 2 && reds.length === 1) {
        return [blacks[0], reds[0], blacks[1]];
      }
      if (reds.length === 2 && blacks.length === 1) {
        return [reds[0], blacks[0], reds[1]];
      }
    }

    if (presentNonTrumpSuits.length === 2) {
      const c1 = this.getSuitColor(presentNonTrumpSuits[0]);
      const c2 = this.getSuitColor(presentNonTrumpSuits[1]);
      if (c1 !== c2) {
        // Prefer starting with Black if present: Black -> Red
        const blackSuit = c1 === 'black' ? presentNonTrumpSuits[0] : presentNonTrumpSuits[1];
        const redSuit = c1 === 'red' ? presentNonTrumpSuits[0] : presentNonTrumpSuits[1];
        return [blackSuit, redSuit];
      }
    }

    return presentNonTrumpSuits;
  }
}
